<template>
  <div class="report-page">
    <header class="report-hero">
      <span class="report-hero__label">食康 · 报告服务</span>
      <h1>您的报告<br>即刻查询<span>.</span></h1>
      <p>{{ entryPid ? '正在为您查询指定检查的报告' : '填写个人信息，查询您的检测报告' }}</p>
      <div class="report-hero__art" aria-hidden="true"><Icon type="ios-document-outline" /><i></i><i></i></div>
    </header>
    <Card dis-hover class="report-card">
      <div class="report-intro">
        <i><Icon type="ios-paper-outline" /></i>
        <div><b>{{ entryPid ? '检测报告查询' : '填写查询信息' }}</b><span>{{ entryPid ? '检查单号：' + entryPid : '请填写姓名，以及手机号或身份证号' }}</span></div>
      </div>
      <Form v-if="!entryPid" label-position="top">
        <FormItem label="姓名">
          <Input v-model="form.trueName" maxlength="20" placeholder="请输入姓名" />
        </FormItem>
        <FormItem label="手机号">
          <Input v-model="form.phone" type="tel" maxlength="11" placeholder="请输入手机号" />
        </FormItem>
        <FormItem label="身份证号">
          <Input v-model="form.idNumber" maxlength="18" placeholder="请输入身份证号" />
        </FormItem>
      </Form>

      <div v-if="errorMessage" class="report-error" role="alert">
        <Icon type="ios-information-circle-outline" />
        <span>{{ errorMessage }}</span>
      </div>

      <div v-if="reportInfo.reportPdfUrl" class="report-result">
        <div class="report-result__title">已查询到报告</div>
        <div class="report-result__meta">检查单号：{{ reportInfo.serialNum || '--' }}</div>
        <div class="report-result__meta">更新时间：{{ reportInfo.updateDate || '--' }}</div>
      </div>

      <div class="report-actions">
        <Button type="primary" long :loading="loading || initializing" @click="queryReport"><Icon type="ios-search" /> 查询并打开报告</Button>
      </div>
    </Card>
  </div>
</template>

<script>
import Qs from 'qs'
import { getAuthMeta } from '../utils/auth'
import { ensureH5Session, readEntryQuery } from '../utils/session'

const autoOpenedEntries = new Set()

function normalizeDigits (value, maxLength) {
  return String(value || '').replace(/\D/g, '').slice(0, maxLength)
}

export default {
  name: 'ReportQuery',
  data () {
    return {
      loading: false,
      initializing: false,
      errorMessage: '',
      form: {
        trueName: '',
        phone: '',
        idNumber: ''
      },
      reportInfo: {
        reportPdfUrl: '',
        serialNum: '',
        updateDate: ''
      }
    }
  },
  watch: {
    '$route.fullPath' () {
      this.initializeReport()
    },
    'form.phone' (value) {
      const normalized = normalizeDigits(value, 11)
      if (normalized !== value) {
        this.form.phone = normalized
      }
    },
    'form.idNumber' (value) {
      const normalized = String(value || '').trim().toUpperCase()
      if (normalized !== value) {
        this.form.idNumber = normalized
      }
    }
  },
  computed: {
    entryPid () {
      return String(readEntryQuery(this.$route).pid || '').trim()
    }
  },
  created () {
    this.initializeReport()
  },
  methods: {
    async initializeReport () {
      this.initializing = true
      this.errorMessage = ''
      this.form = { trueName: '', phone: '', idNumber: '' }
      this.reportInfo = { reportPdfUrl: '', serialNum: '', updateDate: '' }
      try {
        await this.ensureToken()
        this.fillFromRoute()
        if (this.entryPid) {
          const key = JSON.stringify([getAuthMeta().userId, this.$route.fullPath])
          if (!autoOpenedEntries.has(key)) {
            autoOpenedEntries.add(key)
            await this.queryReport()
          }
        } else {
          await this.prefillFromPatient()
        }
      } catch (error) {
        this.errorMessage = error.message || '登录态已失效，请重新进入页面'
      } finally {
        this.initializing = false
      }
    },
    fillFromRoute () {
      const query = readEntryQuery(this.$route)
      this.form.trueName = query.trueName || ''
      this.form.phone = query.phone || ''
      this.form.idNumber = query.idNumber || ''

      const authMeta = getAuthMeta()
      if (!this.form.trueName && authMeta.trueName) {
        this.form.trueName = authMeta.trueName
      }
      if (!this.form.phone && authMeta.phone) {
        this.form.phone = authMeta.phone
      }
      if (!this.form.idNumber && authMeta.idCard) {
        this.form.idNumber = authMeta.idCard
      }
    },
    async ensureToken () {
      await ensureH5Session(this)
    },
    async prefillFromPatient () {
      const query = readEntryQuery(this.$route)
      const patientId = query.patientId || ''
      const code = query.code || query.checkId || ''
      let data = null
      if (patientId || code) {
        const res = await this.$http.get('/h5/patient/getPatientInfoDetail', {
          params: {
            patientId: patientId,
            code: code
          }
        })
        if (res.result === '1' && res.data) {
          data = res.data
        }
      } else {
        const latestRes = await this.$http.get('/h5/patient/getLatestPatient')
        if (latestRes.result === '1' && latestRes.data && latestRes.data.ID) {
          data = latestRes.data
        }
      }
      if (!data) {
        return
      }
      if (!this.form.trueName && data.TRUE_NAME) {
        this.form.trueName = data.TRUE_NAME
      }
      if (!this.form.phone && data.PHONE) {
        this.form.phone = data.PHONE
      }
      if (!this.form.idNumber && data.ID_NUMBER) {
        this.form.idNumber = String(data.ID_NUMBER).trim().toUpperCase()
      }
    },
    buildQueryParams () {
      if (this.entryPid) return { ORDER_NUM: this.entryPid }
      const trueName = String(this.form.trueName || '').trim()
      const phone = normalizeDigits(this.form.phone, 11)
      const idNumber = String(this.form.idNumber || '').trim().toUpperCase()

      if (!trueName) {
        throw new Error('请输入姓名')
      }
      if (!phone && !idNumber) {
        throw new Error('请至少填写手机号或身份证号')
      }
      return {
        name: trueName,
        phone: phone,
        idnum: idNumber
      }
    },
    openReport (url) {
      if (!url) {
        throw new Error('未获取到报告地址')
      }
      this.$router.push({
        name: 'PdfPreview',
        query: { url: url, title: '检测报告' }
      })
    },
    async queryReport () {
      if (this.loading) return
      this.loading = true
      this.errorMessage = ''
      this.reportInfo = { reportPdfUrl: '', serialNum: '', updateDate: '' }
      try {
        await this.ensureToken()
        const params = this.buildQueryParams()
        const res = await this.$http.post('/esophagus/getEpsSnReport', Qs.stringify(params))
        if (res.result !== '1' || !res.data || !res.data.reportPdfUrl) {
          const businessMessage = typeof res.data === 'string' ? res.data : ''
          throw new Error(businessMessage || res.desc || '未查询到报告')
        }
        this.reportInfo = {
          reportPdfUrl: res.data.reportPdfUrl || '',
          serialNum: res.data.SERIAL_NUM || res.data.serialNum || '',
          updateDate: res.data.updateDate || ''
        }
        this.$Message.success('查询成功，正在打开报告')
        this.openReport(this.reportInfo.reportPdfUrl)
      } catch (error) {
        this.errorMessage = error.message || '查询失败'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.report-page {
  min-height: 100vh;
  padding: 12px 16px calc(82px + constant(safe-area-inset-bottom));
  padding-bottom: calc(82px + env(safe-area-inset-bottom));
  box-sizing: border-box;
  background: radial-gradient(ellipse at 90% 5%, #d5eaff, transparent 55%), linear-gradient(180deg, #eaf4ff, #f5f8fc 75%);
}

.report-hero { position: relative; max-width: 640px; margin: 0 auto; padding: 24px 8px 32px; overflow: hidden; }
.report-hero__label { color: #477299; font-size: 12px; letter-spacing: 2px; }
.report-hero h1 { position: relative; z-index: 1; margin: 22px 0 13px; color: #123657; font-size: 36px; line-height: 1.28; letter-spacing: -1px; font-weight: 600; }
.report-hero h1 span { color: #007de9; }
.report-hero p { position: relative; z-index: 1; color: #617e98; font-size: 12px; }
.report-hero__art { position: absolute; right: 12px; top: 56px; width: 110px; height: 120px; display: grid; place-items: center; border: 1px solid #fff; border-radius: 23px; color: #007de9; background: linear-gradient(140deg, #ffffffed, #b6ddff80); box-shadow: 10px 14px 30px #007de91a; transform: rotate(10deg); }
.report-hero__art > .ivu-icon { font-size: 70px; }
.report-hero__art > i:not(.ivu-icon) { position: absolute; width: 170px; height: 170px; border: 1px solid #007de91c; border-radius: 50%; pointer-events: none; }
.report-hero__art > i:last-child { width: 220px; height: 220px; }

.report-card {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
  border: 1px solid #ffffff;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 14px 45px rgba(27, 89, 147, .07);
}

.report-intro {
  margin: 0 0 22px;
  padding: 2px 0 18px;
  border-bottom: 1px solid #eef1f5;
  display: flex;
  align-items: center;
  gap: 12px;
}

.report-intro i {
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  color: var(--wpc-primary);
  background: var(--wpc-primary-soft);
  font-size: 25px;
}

.report-intro b,
.report-intro span { display: block; }
.report-intro b { color: var(--wpc-text); font-size: 17px; }
.report-intro span { margin-top: 4px; color: var(--wpc-text-muted); font-size: 12px; }

.report-card /deep/ .ivu-card-body { padding: 20px 18px; }
.report-card /deep/ .ivu-form-item-label { padding-bottom: 8px; color: #475467; font-size: 14px; font-weight: 500; }
.report-card /deep/ .ivu-input { height: 44px; padding: 10px 12px; border-color: #dfe3e8; border-radius: 8px; font-size: 16px; }

.advanced-trigger {
  width: 100%;
  margin: -2px 0 18px;
  padding: 11px 12px;
  border: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--wpc-primary);
  background: #f6f9fd;
  font-size: 13px;
}

.report-result {
  margin-top: 8px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f6fbff;
  border: 1px solid #d8ebff;
}

.report-error {
  margin-top: 8px;
  padding: 12px 14px;
  border: 1px solid #f2d3d6;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: #b5474f;
  background: #fff7f7;
  font-size: 13px;
  line-height: 1.6;
  word-break: break-word;
}

.report-error > i { flex: 0 0 auto; margin-top: 1px; font-size: 18px; }

.report-result__title {
  color: var(--wpc-primary);
  font-size: 16px;
  font-weight: 600;
}

.report-result__meta {
  margin-top: 6px;
  color: #63738a;
  font-size: 14px;
}

.report-actions {
  margin-top: 22px;
}
.report-actions /deep/ .ivu-btn { height: 48px; border-radius: 12px; font-size: 15px; box-shadow: 0 7px 16px #007de926; }
.report-card /deep/ .ivu-input { background: #f8fbff; border-color: #dbe8f4; border-radius: 11px; height: 48px; }
.report-card /deep/ .ivu-input:focus { border-color: #007de9; background: #fff; box-shadow: 0 0 0 3px #007de912; }

@media (max-width: 340px) {
  .report-page { padding-right: 10px; padding-left: 10px; }
  .report-card /deep/ .ivu-card-body { padding-right: 14px; padding-left: 14px; }
  .report-intro { gap: 10px; }
  .report-intro span { max-width: 210px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}

</style>



// WEBPACK FOOTER //
// src/views/ReportQuery.vue